import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../shared/custom-ui';
import ModernPageHeader from './components/ModernPageHeader';
import WorklistLayout from './components/WorklistLayout';
import useWorklistScale from './hooks/useWorklistScale';
import UnifiedPagination from '../../shared/custom-ui/UnifiedPagination';
import ModernWorklistForm from './components/ModernWorklistForm';
import ApiServices from '../../shared/ApiServices/ApiServices';
import RadiologistStatsModal from '../Dashboard/RadiologistStatsModal';
import { V2Button } from '../../shared/custom-ui/v2-components';
import {
    RefreshCw,
    Plus,
    Filter,
    Download,
    TrendingUp,
    Award,
    Calendar,
    Clock,
    AlertCircle,
    CheckCircle2,
} from 'lucide-react';
import './workListTable.css';

const Icons = { RefreshCw, Plus, Filter, Download, TrendingUp, Award, Calendar, Clock, AlertCircle, CheckCircle2 } as any;

export default function WorklistTable() {
    const [loading, setLoading] = useState(false);
    const [worklist, setWorklist] = useState<any[]>([]);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [lastTokenStack, setLastTokenStack] = useState<string[]>([]);
    const [currentToken, setCurrentToken] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingItem, setEditingItem] = useState<any | null>(null);
    const [limit, setLimit] = useState(10);
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [selectedRadiologist, setSelectedRadiologist] = useState<{ id: string; name: string } | null>(null);

    useEffect(() => {
        fetchWorklist(null);
    }, []);

    // Re-fetch when limit (per-page) changes
    useEffect(() => {
        if (limit !== 10 || currentPage !== 1) {
            fetchWorklist(null);
        }
    }, [limit]);

    const fetchWorklist = async (token: string | null = null) => {
        try {
            setLoading(true);
            const response = await ApiServices.getWorklist(undefined, limit, token);
            setWorklist(response.tasks || []);
            setCurrentToken(token);
            setNextToken(response.pagination?.last_key || null);
            setHasMore(response.pagination?.has_more || false);
        } catch (error) {
            console.error('Failed to fetch worklist:', error);
            setWorklist([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (token: string | null, direction: 'next' | 'prev') => {
        if (direction === 'next' && token) {
            if (currentToken) setLastTokenStack(prev => [...prev, currentToken]);
            setCurrentPage(prev => prev + 1);
            fetchWorklist(token);
        } else if (direction === 'prev') {
            const previousToken = lastTokenStack[lastTokenStack.length - 1] || null;
            setLastTokenStack(prev => prev.slice(0, -1));
            setCurrentPage(prev => Math.max(prev - 1, 1));
            fetchWorklist(previousToken);
        }
    };

    const handleRefresh = () => {
        setCurrentPage(1);
        setLastTokenStack([]);
        setCurrentToken(null);
        fetchWorklist(null);
    };

    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setCurrentPage(1);
        setLastTokenStack([]);
        setCurrentToken(null);
        // Re-fetch with new limit on next render
    };

    const handleFormSuccess = () => {
        fetchWorklist(currentToken);
        setShowForm(false);
        setEditingItem(null);
    };

    const handleEdit = (item: any) => {
        setEditingItem(item);
        setShowForm(true);
    };

    const handleDelete = async (task: any) => {
        try {
            setLoading(true);
            await ApiServices.deleteWorklistTask(task.PK, task.rad_id);
            fetchWorklist(currentToken);
        } catch (error) {
            console.error('Delete failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (row: any) => {
        if (row.rad_id && row.radiologist_name) {
            setSelectedRadiologist({
                id: row.rad_id,
                name: row.radiologist_name,
            });
            setShowStatsModal(true);
        }
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const scale = useWorklistScale(containerRef);

    return (
        <div className="relative">
            <ModernPageHeader
                title="Radiologist Worklist"
                subtitle="Track study assignments and progress with real-time status updates"
                actions={
                    <>
                        <V2Button
                            variant="secondary"
                            size="small"
                            leftIcon={<Icons.RefreshCw className="h-4 w-4" />}
                            onClick={handleRefresh}
                            disabled={loading}
                            title="Refresh worklist"
                        >
                            Refresh
                        </V2Button>
                        <V2Button
                            variant="primary"
                            size="small"
                            onClick={() => {
                                setEditingItem(null);
                                setShowForm(true);
                            }}
                            leftIcon={<Icons.Plus className="h-4 w-4" />}
                        >
                            New Task
                        </V2Button>
                    </>
                }
            />

            <div ref={containerRef}>
                <WorklistLayout
                    data={worklist}
                    loading={loading}
                    onRowClick={handleRowClick}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    scale={scale}
                />

                <div className="mt-4" style={{ zoom: scale }}>
                    <UnifiedPagination
                        currentPage={currentPage}
                        onPrevious={() => handlePageChange(null, 'prev')}
                        onNext={() => handlePageChange(nextToken, 'next')}
                        canGoBack={currentPage > 1}
                        hasMore={hasMore && !!nextToken}
                        perPage={limit}
                        perPageOptions={[10, 25, 50]}
                        onPerPageChange={handleLimitChange}
                    />
                </div>
            </div>

            <ModernWorklistForm
                isOpen={showForm}
                onClose={() => {
                    setShowForm(false);
                    setEditingItem(null);
                }}
                initialData={editingItem}
                isEditMode={!!editingItem}
                onSuccess={handleFormSuccess}
            />

            <RadiologistStatsModal
                isOpen={showStatsModal}
                onClose={() => {
                    setShowStatsModal(false);
                    setSelectedRadiologist(null);
                }}
                radiologistId={selectedRadiologist?.id}
                radiologistName={selectedRadiologist?.name}
            />
        </div>
    );
}
